import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { cn } from '@/utils/cn';
import { editorStyles, proseMirrorStyles } from './styles';

interface RichTextDisplayProps {
  content: string;
  className?: string;
  emptyMessage?: string;
  sanitize?: boolean;
}

// Configure DOMPurify for safe HTML
const sanitizeConfig: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'blockquote', 'pre', 'code',
    'a', 'span',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class', 'colspan', 'rowspan'],
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ['target'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
};

// eslint-disable-next-line react-refresh/only-export-components
export const sanitizeHtml = (html: string): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return DOMPurify.sanitize(html, sanitizeConfig as any) as unknown as string;
};

export const RichTextDisplay: React.FC<RichTextDisplayProps> = ({
  content,
  className,
  emptyMessage = 'No content',
  sanitize = true,
}) => {
  const sanitizedContent = useMemo(() => {
    if (!content || content.trim() === '' || content === '<p></p>') {
      return null;
    }
    return sanitize ? sanitizeHtml(content) : content;
  }, [content, sanitize]);

  if (!sanitizedContent) {
    return (
      <p className={cn('text-secondary-500 dark:text-secondary-400 italic', className)}>
        {emptyMessage}
      </p>
    );
  }

  return (
    <>
      <style>{proseMirrorStyles}</style>
      <div
        className={cn(
          editorStyles.prose,
          'ProseMirror',
          className
        )}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    </>
  );
};

export default RichTextDisplay;
